import React, { useState, useCallback, useContext } from "react";
import { ReactComponent as PlusIcons } from "../../../../../assets/icons/fi-plus-circle.svg";
import CustomInputFile from "../../../components/CustomInputFile";
import { toast } from "react-toastify";
import CustomSelect from "../../../components/CustomSelect";
import { process_content } from "../../../../../constants/PROCESS_CONTENT";
import InputDate from "../../../components/InputDate";
import { processService } from "../../../../../services/api/processService";
import { AuthContext } from "../../../../../contexts/AuthContext";
import Modal from "../../../components/Modal";

// Alert Modal Component
const AlertModal = ({ isOpen, onClose, message }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title="Thông báo"
    maxWidth="400px"
    primaryButtonText="Đồng ý"
    primaryButtonOnClick={onClose}
    showCancelButton={false}
  >
    <div className="px-6 py-4">
      <p className="text-gray-700">{message}</p>
    </div>
  </Modal>
);

// Confirm Modal Component
const ConfirmModal = ({ isOpen, onClose, onConfirm, message }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title="Xác nhận"
    maxWidth="400px"
    primaryButtonText="Đồng ý"
    primaryButtonOnClick={onConfirm}
    cancelButtonText="Hủy"
  >
    <div className="px-6 py-4">
      <p className="text-gray-700">{message}</p>
    </div>
  </Modal>
);

const getFileNameFromUrl = (url) => {
  return decodeURIComponent(url.split("-").pop());
};

const formatDateToDDMMYYYY = (completionDate) => {
  const d = new Date(completionDate);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const parseDateToYYYYMMDD = (completionDate) => {
  const [day, month, year] = completionDate.split("/");
  return `${year}-${month}-${day}`;
};

const PopupEdit = ({ isOpen, onClose, data, onSave, profileId, token }) => {
  const { user } = useContext(AuthContext);
  const [isAddRow, setIsAddRow] = useState(false);

  // Modal states
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: "" });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    message: "",
    onConfirm: null,
  });

  const [editData, setEditData] = useState(() =>
    data
      .map((row) => ({
        ...row,
        isNew: row.isNew || false,
        completionDate: row.completionDate
          ? formatDateToDDMMYYYY(new Date(row.completionDate))
          : "",
        isEditing: false,
        pdfFile: row.pdfFile || null,
      }))
      .reverse(),
  );

  const addRow = () => {
    if (editData.some((row) => row.isNew)) {
      setAlertModal({
        isOpen: true,
        message:
          "Bạn cần hoàn thành chỉnh sửa hàng hiện tại trước khi thêm hàng mới.",
      });
      return;
    }

    setIsAddRow(true);
    setEditData((prev) => [
      {
        name: "Nội dung mới",
        completionDate: formatDateToDDMMYYYY(new Date()),
        pdfFile: null,
        isNew: true,
      },
      ...prev,
    ]);
    setTimeout(() => {
      const tableContainer = document.getElementById("table-container");
      if (tableContainer) {
        tableContainer.scrollTop = 0;
      }
    }, 100);
  };

  const handleInputChange = useCallback((rowIndex, field, value) => {
    setEditData((prev) =>
      prev.map((row, index) =>
        index === rowIndex ? { ...row, [field]: value } : row,
      ),
    );
  }, []);

  const handleDeleteClick = (rowIndex) => {
    setConfirmModal({
      isOpen: true,
      message: "Bạn có chắc chắn muốn xóa hàng này không?",
      onConfirm: () => {
        setEditData((prev) => prev.filter((_, index) => index !== rowIndex));
        setIsAddRow(false);
        setConfirmModal({ isOpen: false, message: "", onConfirm: null });
      },
    });
  };

  const handleSave = () => {
    const newRows = editData
      .filter((row) => row.isNew)
      .map((row) => ({
        ...row,
        name: row.name.value,
        completionDate: parseDateToYYYYMMDD(row.completionDate),
        pdfFile: row.pdfFile ? row.pdfFile : null,
      }));

    if (newRows.length === 0) {
      setAlertModal({
        isOpen: true,
        message: "Không có dữ liệu mới để cập nhật.",
      });
      return;
    }
    if (!newRows[0].name) {
      toast.error("Vui lòng chọn tên nội dung xử lý.");
      return;
    }
    if (newRows[0].pdfFile) {
      onSave(newRows[0]);
      onClose();
    } else {
      toast.error("Chưa có file PDF nào được chọn.");
      return;
    }
  };

  const handleOldEditClick = (_id) => {
    setEditData((prev) =>
      prev.map((row) =>
        row._id === _id
          ? {
              ...row,
              name: { value: row.processContent, label: row.processContent },
              isEditing: true,
            }
          : row,
      ),
    );
  };

  const handleOldEditCancel = async (_id) => {
    setConfirmModal({
      isOpen: true,
      message: "Bạn có chắc chắn hủy thao tác này không?",
      onConfirm: () => {
        setEditData((prev) =>
          prev.map((row) =>
            row._id === _id
              ? {
                  ...row,
                  isEditing: false,
                }
              : row,
          ),
        );
        setConfirmModal({ isOpen: false, message: "", onConfirm: null });
      },
    });
  };

  const handleOldDeleteClick = async (_id) => {
    setConfirmModal({
      isOpen: true,
      message: "Bạn có chắc chắn muốn xóa hàng này không?",
      onConfirm: async () => {
        try {
          await processService.delete(profileId, _id, token);
          setEditData((prev) => prev.filter((row) => row._id !== _id));
          toast.success(
            "Xóa tiến trình thành công, vui lòng làm mới trang để xem kết quả!",
          );
        } catch (error) {
          toast.dismiss();
          toast.error(
            error.response
              ? `${error.response?.data?.message}`
              : "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
          );
          console.error("Error:", error);
        } finally {
          setConfirmModal({ isOpen: false, message: "", onConfirm: null });
        }
      },
    });
  };

  const handleSaveEdit = async (_id) => {
    try {
      const updatedRow = editData.find((row) => row._id === _id);
      if (!updatedRow) {
        toast.error("Không tìm thấy dữ liệu để cập nhật.");
        return;
      }

      const formData = new FormData();
      formData.append("name", updatedRow.name.value);
      formData.append("completionDate", updatedRow.completionDate);

      if (updatedRow.pdfFile instanceof File) {
        formData.append("pdfFile", updatedRow.pdfFile);
      }

      await processService.update(_id, formData, token);

      setEditData((prev) =>
        prev.map((row) =>
          row._id === _id
            ? {
                ...row,
                processContent: updatedRow.name.value,
                completionDate: updatedRow.completionDate,
                isEditing: false,
              }
            : row,
        ),
      );

      toast.success(
        "Cập nhật tiến trình thành công, vui lòng làm mới trang để xem thay đổi!",
      );
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response
          ? `${error.response?.data?.message}`
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Error:", error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tiến trình hồ sơ"
      maxWidth="800px"
      primaryButtonText={isAddRow ? "Thêm tiến trình mới" : null}
      primaryButtonOnClick={isAddRow ? handleSave : null}
      cancelButtonText={isAddRow ? null : "Hủy"}
      showCancelButton={!isAddRow}
    >
      <div
        id="table-container"
        className="min-h-[50vh] max-h-[50vh] overflow-y-auto overflow-x-hidden relative"
      >
        <table className="w-full text-left border table-fixed">
          <thead className="sticky -top-0 bg-white z-50 shadow-md">
            <tr className="bg-gray-200 font-sf-medium">
              <th className="p-3 w-[50px] text-center">STT</th>
              <th className="p-3 w-1/3">Nội dung xử lý</th>
              <th className="p-3 w-1/3">Ngày hoàn thành</th>
              <th className="p-3 w-1/3">Tài liệu</th>
              <th className="p-3 w-[50px]"></th>
            </tr>
          </thead>
          <tbody>
            {editData.map((row, rowIndex) => (
              <tr
                key={row._id || rowIndex}
                className="hover:bg-blue-50 border-b h-[72px]"
              >
                <td className="p-3 w-[50px] text-center">
                  {editData.length - rowIndex}
                </td>

                {row.isNew || row.isEditing ? (
                  <>
                    <td className="p-3 w-1/3">
                      <CustomSelect
                        options={process_content}
                        placeholder="Chọn nội dung xử lý"
                        value={row.name}
                        onChange={(newValue) =>
                          handleInputChange(rowIndex, "name", newValue)
                        }
                      />
                    </td>
                    <td className="p-3 w-1/3">
                      <InputDate
                        value={row.completionDate}
                        onChange={(formattedDate) =>
                          handleInputChange(
                            rowIndex,
                            "completionDate",
                            formattedDate,
                          )
                        }
                      />
                    </td>
                    <td className="p-3 w-1/3">
                      <CustomInputFile
                        onFileSelect={(file) =>
                          handleInputChange(rowIndex, "pdfFile", file)
                        }
                        chooseFile={
                          row.pdfUrl ? getFileNameFromUrl(row.pdfUrl) : null
                        }
                      />
                    </td>
                    <td>
                      {row._id ? (
                        <div className="h-full flex gap-2 justify-center items-center">
                          <button onClick={() => handleSaveEdit(row._id)}>
                            💾
                          </button>
                          <button onClick={() => handleOldEditCancel(row._id)}>
                            <img src="/icons/x-close-circle.svg" alt="" />
                          </button>
                        </div>
                      ) : (
                        <img
                          src="/icons/trash-can.svg"
                          alt="delete icon"
                          className="w-5 h-5 cursor-pointer"
                          onClick={() => handleDeleteClick(rowIndex)}
                        />
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-3 w-1/3">{row.processContent}</td>
                    <td className="p-3 w-1/3">{row.completionDate}</td>
                    <td className="p-3 w-1/3 text-blue-600">
                      {row.pdfUrl ? (
                        <a href={row.pdfUrl} className="hover:underline">
                          Tài liệu
                        </a>
                      ) : (
                        "Không có tài liệu"
                      )}
                    </td>
                    <td>
                      {user?.role === "SuperAdmin" && (
                        <div className="flex h-full items-center justify-center gap-2">
                          <img
                            src="/icons/edit-black.svg"
                            alt="edit icon"
                            className="w-5 h-5 cursor-pointer"
                            onClick={() => handleOldEditClick(row._id)}
                          />
                          <img
                            src="/icons/trash-can.svg"
                            alt="delete icon"
                            className="w-5 h-5 cursor-pointer"
                            onClick={() => handleOldDeleteClick(row._id)}
                          />
                        </div>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        onClick={addRow}
        className="flex items-center gap-2 text-[#0954E9] font-bold hover:text-gray-600 p-2 mt-4 cursor-pointer"
      >
        <PlusIcons /> Thêm hàng
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ isOpen: false, message: "" })}
        message={alertModal.message}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal({ isOpen: false, message: "", onConfirm: null })
        }
        onConfirm={confirmModal.onConfirm}
        message={confirmModal.message}
      />
    </Modal>
  );
};

export default PopupEdit;
